import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { Briefcase, Calendar, Loader2, Mail, MapPin, MessageCircle, Phone, Save, Sparkles, X } from "lucide-react";
import { Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { useUser } from "../../hooks/use-user";
import useApiRequest from "../../lib/hooks/useRequest";
import {
  ApplicantCreationEntitiesByCompanyLocationIdResponseDto,
  ApplicantCreationEntitiesResponseDto,
  ApplicantDetailsResponseDto,
  ApplicantLog,
  ApplicantLogEvent,
  ApplicantMailTemplatesResponseDto,
  ApplicantStatusSelectableEntityResponseDto,
  CreateApplicantRequestDto,
  Genders,
  JobOfferSelectableEntityResponseDto,
  ValutationEnum,
} from "../../lib/interfaces/applicant";
import { cn } from "../../lib/utils/cn";
import { getInitials } from "../../lib/utils/initials";
import { Button } from "../../ui/components/ui/button";
import { Dialog, DialogContent } from "../../ui/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../ui/components/ui/form";
import { Input } from "../../ui/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../../ui/components/ui/select";
import { Textarea } from "../../ui/components/ui/textarea";
import ApplicantDocuments from "./ApplicantDocuments";
import MailLogDialog from "./MailLogDialog";
import MailModal from "./MailModal";
import WhatsAppChatPanel from "./WhatsAppChatPanel";
import WhatsAppMessageDialog from "./WhatsAppMessageDialog";

// Header banner gradient — derived from the app's sidebar CI (sky -> indigo).
const BANNER_GRADIENT = "linear-gradient(100deg, #5a3ec8 0%, #4a52c9 42%, #1f93d1 100%)";

const LABEL_CLASS = "text-xs font-semibold uppercase tracking-wide text-slate-400";

// Ampel: maps the applicant valuation onto three semantic tones (CI-aligned).
type AmpelTone = "good" | "warn" | "crit";

const VALUATION_META: Record<string, { label: string; tone: AmpelTone }> = {
  GOOD: { label: "Gut", tone: "good" },
  NEUTRAL: { label: "Mittel", tone: "warn" },
  BAD: { label: "Schlecht", tone: "crit" },
};

const AMPEL_ORDER: (keyof typeof VALUATION_META)[] = ["GOOD", "NEUTRAL", "BAD"];

const TONE: Record<AmpelTone, { dot: string; activeSeg: string }> = {
  good: { dot: "bg-[#2fb563]", activeSeg: "border-[#2fb563] bg-[#e7f7ec] text-[#1f7a44] ring-2 ring-[#2fb563]/20" },
  warn: { dot: "bg-[#eaa028]", activeSeg: "border-[#eaa028] bg-[#fdf3e2] text-[#a75e12] ring-2 ring-[#eaa028]/20" },
  crit: { dot: "bg-[#e35700]", activeSeg: "border-[#e35700] bg-[#fceadf] text-[#b5470f] ring-2 ring-[#e35700]/20" },
};

// Status options carry no category from the backend — group them by their German
// label so the dropdown can render section headings.
const STATUS_CATEGORIES: { label: string; match: (value: string) => boolean }[] = [
  { label: "Offen", match: (v) => v === "Offen" || v === "Bewerber nicht erreicht" },
  { label: "Terminiert", match: (v) => v === "Terminiert" },
  { label: "Einladung", match: (v) => v.startsWith("Einladung zum") },
  { label: "Absage", match: (v) => v.startsWith("Absage") },
  { label: "Bewerberpool", match: (v) => v.includes("Bewerberpool") },
  { label: "Eingestellt", match: (v) => v === "Eingestellt" },
];

const groupStatusOptions = (statuses: ApplicantStatusSelectableEntityResponseDto[]) => {
  const claimed = new Set<string>();
  const groups = STATUS_CATEGORIES.map((category) => {
    const items = statuses.filter((status) => !claimed.has(status.id) && category.match(status.value));
    items.forEach((status) => claimed.add(status.id));
    return { label: category.label, items };
  }).filter((group) => group.items.length > 0);

  // Anything the backend adds that we don't recognise still shows up.
  const rest = statuses.filter((status) => !claimed.has(status.id));
  if (rest.length > 0) {
    groups.push({ label: "Weitere", items: rest });
  }
  return groups;
};

// The location/job-offer selects use the sentinel "undefined" for "nothing picked";
// the API expects an empty value there.
const normalizeAssignmentId = (value?: string | null) => (value && value !== "undefined" ? value : "");

const formatDate = (value?: string) => {
  if (!value) return "—";
  const date = new Date(value);
  return isNaN(date.getTime()) ? value : format(date, "dd.MM.yyyy");
};

// Appointments carry a time; only render it when it is meaningful (not midnight).
const formatAppointment = (value?: string) => {
  if (!value) return undefined;
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  const hasTime = date.getHours() !== 0 || date.getMinutes() !== 0;
  return format(date, hasTime ? "dd.MM.yyyy, HH:mm 'Uhr'" : "dd.MM.yyyy");
};

// Kennenlerntermin resolution:
//  1. a real appointmentDate wins,
//  2. otherwise, for WhatsApp-AI leads, show the AI appointment (UTC startsAt rendered in the
//     viewer's local timezone) or a "no appointment yet" hint,
//  3. otherwise the field is hidden entirely (returns null).
const resolveKennenlerntermin = (item: ApplicantDetailsResponseDto): string | null => {
  const scheduled = formatAppointment(item.appointmentDate);
  if (scheduled) return scheduled;
  if (!item.isAiWhatsAppApplicant) return null;

  const appointment = item.aiWhatsAppAppointment;
  // Abgesagte Termine klar als solche kennzeichnen (nicht mehr wie aktiv anzeigen).
  if (appointment?.status === "cancelled") {
    return appointment.cancelReason ? `Abgesagt · ${appointment.cancelReason}` : "Abgesagt";
  }
  const booked = appointment?.status === "booked" ? formatAppointment(appointment.startsAt) : undefined;
  return booked ?? "Noch kein Termin vereinbart";
};

const SectionCard = ({ title, right, children }: { title: string; right?: ReactNode; children: ReactNode }) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
    <div className="mb-5 flex items-center justify-between gap-3">
      <h2 className="text-base font-bold text-slate-900">{title}</h2>
      {right}
    </div>
    {children}
  </section>
);

const ReadonlyField = ({ label, value }: { label: string; value?: string }) => (
  <div className="space-y-2">
    <div className={LABEL_CLASS}>{label}</div>
    <div className="flex min-h-[42px] items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-800">
      {value?.trim() ? value : "—"}
    </div>
  </div>
);

const MetaItem = ({ icon, label, value }: { icon: ReactNode; label: string; value: string }) => (
  <div className="flex items-center gap-3 py-4 lg:border-l lg:border-white/12 lg:pl-5 lg:first:border-l-0 lg:first:pl-0">
    <div className="grid h-9 w-9 flex-none place-items-center rounded-lg bg-white/15">{icon}</div>
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-white/70">{label}</div>
      <div className="truncate text-sm font-semibold">{value}</div>
    </div>
  </div>
);

const ApplicantEditor = () => {
  const [item, setItem] = useState<ApplicantDetailsResponseDto>();
  const [creationEntities, setCreationEntities] = useState<ApplicantCreationEntitiesResponseDto>();
  const { id } = useParams();
  const { apiRequest } = useApiRequest();

  const getItem = useCallback(async () => {
    const response = await apiRequest<ApplicantDetailsResponseDto>(`applicants/${id}`, "GET");

    if (response.data) {
      setItem(response.data);
      setCreationEntities(response.data.selectableEntities);
    }
  }, [apiRequest, id]);

  const getEntities = useCallback(async () => {
    const creationEntitiesResponse = await apiRequest<ApplicantCreationEntitiesResponseDto>(
      "applicants/creation-entities",
      "GET",
    );
    if (creationEntitiesResponse.data) {
      setCreationEntities(creationEntitiesResponse.data);
    }
  }, [apiRequest]);

  useEffect(() => {
    // if there is an id, get the item
    if (id) {
      getItem();
    } else {
      getEntities();
    }
  }, [getEntities, getItem, id]);

  const isLoading = !creationEntities || (id && !item);

  return (
    <div className="min-h-full px-6 py-6 pb-28">
      {isLoading ? (
        <div className="flex h-[60vh] items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : (
        <PositionForm item={item} creationEntities={creationEntities!} onSaved={getItem} />
      )}
    </div>
  );
};

const PositionForm = ({
  creationEntities,
  item,
  onSaved,
}: {
  creationEntities: ApplicantCreationEntitiesResponseDto;
  item?: ApplicantDetailsResponseDto;
  onSaved?: () => void;
}) => {
  const { apiRequest } = useApiRequest();
  const [selectedLocationsJobOffers, setSelectedLocationsJobOffers] = useState<JobOfferSelectableEntityResponseDto[]>(
    creationEntities.jobOffers ?? [],
  );
  const { user } = useUser();
  const isEditable =
    (user?.permissionApplicants === "FULL" ||
      user?.permissionApplicants === "WRITE" ||
      user?.permissionOrganizationAdmin) &&
    (item ? item.isEditable : true);

  const [jobOffersLoading, setJobOffersLoading] = useState(false);
  const [emailIsOpen, setEmailIsOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [waMessageOpen, setWaMessageOpen] = useState(false);
  const [selectedMailLogId, setSelectedMailLogId] = useState<string | null>(null);
  const [isMailLogDialogOpen, setIsMailLogDialogOpen] = useState(false);

  const telRegex = /^\+?(\d+|\([0-9]+\))([0-9]*[-()]?){5,13}\d$/;

  const navigate = useNavigate();

  const formSchema = z.object({
    name: z.string().min(3, {
      message: "Bitte geben Sie Ihren Namen ein",
    }),
    gender: z.string().optional(),
    street: z.string().optional(),
    postCode: z.string().optional(),
    placeName: z.string().optional(),
    phone: z.union([
      z.literal(""),
      z
        .string()
        .trim()
        .transform((val) => val.replace(/\s/g, ""))
        .refine((val) => telRegex.test(val), {
          message: "Ungültige Telefonnummer. Geben Sie es in folgendem Format ein: +49123123123",
        }),
    ]),
    mail: z.union([z.literal(""), z.string().email("Bitte geben Sie eine E-Mail Adresse ein")]),
    notes: z.string().optional(),
    belongsToCompanyLocationId: user?.permittedContentWithoutCompanyLocationAssignment
      ? z.string()
      : item
        ? z.string()
        : z.string().min(1, {
            message: "Bitte wählen Sie einen Standort aus",
          }),
    belongsToJobOfferId: z.string(),
    valuation: z.string().optional(),
    source: z.string().min(3, {
      message: "Bitte geben Sie Ihre Quelle ein",
    }),
    status: z.string().min(3, {
      message: "Bitte geben Sie Ihren Status ein",
    }),
  });

  const getJobOffersByLocationId = useCallback(
    async (id: string | undefined): Promise<JobOfferSelectableEntityResponseDto[]> => {
      if (!id) return [];
      setJobOffersLoading(true);
      const res = await apiRequest<ApplicantCreationEntitiesByCompanyLocationIdResponseDto>(
        `applicants/creation-entities/companyLocation/${id}`,
        "GET",
      );
      const jobOffers = res.data?.jobOffers ?? [];
      setSelectedLocationsJobOffers(jobOffers);
      // Always clear, including on a failed request — otherwise the spinner never goes away.
      setJobOffersLoading(false);
      return jobOffers;
    },
    [apiRequest],
  );

  /**
   * Which job offer to select after the location changed. A job offer belongs to exactly one
   * location, so the previous pick can never carry over as-is — but two cases have an obvious
   * answer and save the user a click:
   *
   *  1. Back to where the applicant actually sits → restore their real job offer.
   *  2. The target location has exactly one job offer with the same title → the same job at a
   *     different house, which is the common multi-location case.
   *
   * Anything else stays empty on purpose: a position nobody consciously picked is the one piece
   * of information the whole conversation with the applicant refers to.
   */
  const pickJobOfferForLocation = (
    offers: JobOfferSelectableEntityResponseDto[],
    locationId: string,
    previousTitle: string | undefined,
  ): string => {
    if (
      item?.belongsToCompanyLocationId === locationId &&
      item?.belongsToJobOfferId &&
      offers.some((offer) => offer._id === item.belongsToJobOfferId)
    ) {
      return item.belongsToJobOfferId;
    }

    const wanted = previousTitle?.trim().toLowerCase();
    if (wanted) {
      const sameTitle = offers.filter((offer) => offer.value.trim().toLowerCase() === wanted);
      if (sameTitle.length === 1) return sameTitle[0]._id;
    }

    return "";
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: item?.name ?? "",
      gender: item?.gender ?? "NONE",
      street: item?.street ?? "",
      postCode: item?.postCode ?? "",
      placeName: item?.placeName ?? "",
      phone: item?.phone ?? "",
      mail: item?.mail ?? "",
      notes: item?.notes ?? "",
      belongsToCompanyLocationId: item?.belongsToCompanyLocationId ?? "",
      belongsToJobOfferId: item?.belongsToJobOfferId ?? "",
      valuation: item?.valuation ?? "",
      source: item?.belongsToSource ?? "",
      status: item?.belongsToStatus ?? creationEntities.status.find((status) => status.default)?.id ?? "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    const req: CreateApplicantRequestDto = {
      name: values.name,
      gender: values.gender === "" ? "" : values.gender,
      street: values.street,
      postCode: values.postCode,
      placeName: values.placeName,
      phone: values.phone,
      mail: values.mail === "" ? null : values.mail,
      notes: values.notes,
      belongsToCompanyLocationId:
        values.belongsToCompanyLocationId === "undefined" ? "" : values.belongsToCompanyLocationId,
      belongsToJobOfferId: values.belongsToJobOfferId === "undefined" ? "" : values.belongsToJobOfferId,
      valuation: values.valuation === "" ? undefined : (values.valuation as ValutationEnum),
      belongsToSource: values.source,
      belongsToStatus: values.status,
    };

    if (item) {
      // Standort and Stelle travel on their own endpoint: moving an applicant also has to
      // correct the applicant counters of both locations and their cached statistics, which
      // the general update does not do. Move first — if it fails, the rest is not saved
      // either and the user sees why.
      const targetCompanyLocationId = normalizeAssignmentId(values.belongsToCompanyLocationId);
      const targetJobOfferId = normalizeAssignmentId(values.belongsToJobOfferId);
      const isMoved =
        targetCompanyLocationId !== normalizeAssignmentId(item.belongsToCompanyLocationId) ||
        targetJobOfferId !== normalizeAssignmentId(item.belongsToJobOfferId);

      if (isMoved) {
        const moveResponse = await apiRequest(`applicants/${item._id}/assignment`, "PATCH", {
          body: {
            belongsToCompanyLocationId: targetCompanyLocationId,
            belongsToJobOfferId: targetJobOfferId,
          },
        });
        if (moveResponse.error) return;
      }

      const response = await apiRequest(`applicants/${item._id}`, "PATCH", {
        body: req,
        toast: {
          toastText: isMoved ? "Bewerber wurde verschoben und aktualisiert" : "Bewerber wurde erfolgreich aktualisiert",
        },
      });
      // Stay on the applicant and refresh its data (logs, server-computed fields).
      if (!response.error) {
        onSaved?.();
      }
    } else {
      const response = await apiRequest<{ _id: string }>("applicants", "POST", {
        body: req,
        toast: { toastText: "Bewerber wurde erfolgreich erstellt" },
      });
      // Open the freshly created applicant instead of falling back to the dashboard.
      if (!response.error) {
        const newId = response.data?._id;
        navigate(newId ? `/bewerber/${newId}` : "/bewerber");
      }
    }
  }

  useEffect(() => {
    if (item?.belongsToCompanyLocationId) {
      getJobOffersByLocationId(item.belongsToCompanyLocationId);
    }
  }, [getJobOffersByLocationId, item?.belongsToCompanyLocationId]);

  const watchedName = form.watch("name");
  const watchedPhone = form.watch("phone");
  const watchedValuation = form.watch("valuation");
  const watchedCompanyLocationId = form.watch("belongsToCompanyLocationId");
  const watchedJobOfferId = form.watch("belongsToJobOfferId");

  const assignmentChanged =
    !!item &&
    (normalizeAssignmentId(watchedCompanyLocationId) !== normalizeAssignmentId(item.belongsToCompanyLocationId) ||
      normalizeAssignmentId(watchedJobOfferId) !== normalizeAssignmentId(item.belongsToJobOfferId));

  const displayName = watchedName?.trim() || "Neuer Bewerber";
  const role = item?.jobOfferTitle?.trim() || "Bewerber";
  const phoneValue = (watchedPhone || item?.phone || "").trim();
  const valuationMeta = watchedValuation ? VALUATION_META[watchedValuation] : undefined;
  const kennenlerntermin = item ? resolveKennenlerntermin(item) : null;

  return (
    <Form {...form}>
      <div className="flex flex-col gap-4">
        {/* ------------------------- Header banner ------------------------- */}
        <header
          className={cn(
            "relative isolate overflow-hidden rounded-3xl px-8 pt-6 text-white shadow-lg",
            !item && "pb-6",
          )}
          style={{ background: BANNER_GRADIENT }}
        >
          <div
            className="pointer-events-none absolute inset-0"
            style={{ background: "radial-gradient(900px 300px at 88% -40%, rgba(255,255,255,.18), transparent 60%)" }}
          />
          <div className="relative z-10 flex flex-col items-start justify-between gap-5 md:flex-row md:items-center">
            <div className="flex items-center gap-4">
              <div className="grid h-14 w-14 flex-none place-items-center rounded-2xl border border-white/25 bg-white/15 text-lg font-bold">
                {getInitials(displayName)}
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-2xl font-bold tracking-tight">{displayName}</h1>
                  {valuationMeta && (
                    <span className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/15 px-3 py-1 text-xs font-semibold">
                      <span className={cn("h-2 w-2 rounded-full", TONE[valuationMeta.tone].dot)} />
                      {valuationMeta.label}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm text-white/80">{role}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2.5">
              {phoneValue && (
                <a
                  href={`tel:${phoneValue.replace(/\s/g, "")}`}
                  className="inline-flex items-center gap-2 rounded-[10px] border border-white/30 bg-white/10 px-4 py-2 text-sm font-semibold transition hover:bg-white/20"
                >
                  <Phone className="h-4 w-4" />
                  Anrufen
                </a>
              )}
              {item?.isAiWhatsAppApplicant && (
                <button
                  type="button"
                  onClick={() => setChatOpen(true)}
                  className="inline-flex items-center gap-2 rounded-[10px] border border-white/30 bg-white/10 px-4 py-2 text-sm font-semibold transition hover:bg-white/20"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp-Verlauf
                </button>
              )}
              {item && (
                <button
                  type="button"
                  onClick={() => setEmailIsOpen(true)}
                  disabled={!isEditable}
                  className="inline-flex items-center gap-2 rounded-[10px] bg-[#101a2b] px-4 py-2 text-sm font-semibold shadow-sm transition hover:bg-[#1c2942] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Mail className="h-4 w-4" />
                  Email senden
                </button>
              )}
            </div>
          </div>

          {item && (
            <div className="relative z-10 mt-6 grid grid-cols-1 border-t border-white/15 sm:grid-cols-2 lg:grid-cols-4">
              {kennenlerntermin && (
                <MetaItem
                  icon={<Calendar className="h-[17px] w-[17px]" />}
                  label="Kennenlerntermin"
                  value={kennenlerntermin}
                />
              )}
              <MetaItem icon={<Phone className="h-[17px] w-[17px]" />} label="Telefon" value={phoneValue || "—"} />
              <MetaItem
                icon={<Briefcase className="h-[17px] w-[17px]" />}
                label="Beworben als"
                value={item.jobOfferTitle?.trim() || "—"}
              />
              <MetaItem
                icon={<MapPin className="h-[17px] w-[17px]" />}
                label="Standort"
                value={item.companyLocationTitle?.trim() || "—"}
              />
            </div>
          )}
        </header>

        {/* --------------------- KI WhatsApp summary ----------------------- */}
        {item?.aiWhatsAppSummary && (
          <section
            className="rounded-2xl border border-[#dbe3f6] p-5"
            style={{ background: "linear-gradient(115deg, #eef1ff 0%, #e7f1fd 55%, #e4f5fc 100%)" }}
          >
            <div className="flex items-center gap-2.5">
              <span className="grid h-6 w-6 place-items-center rounded-lg bg-[#5257d6]">
                <Sparkles className="h-3.5 w-3.5 text-white" />
              </span>
              <span className="text-[15px] font-bold text-[#2b2f6b]">KI WhatsApp Zusammenfassung</span>
            </div>
            <p className="mt-3 max-w-[108ch] text-[14.5px] text-[#2f3652]">{item.aiWhatsAppSummary}</p>
            <p className="mt-2 text-xs text-[#7a80a6]">Automatisch von der WhatsApp KI aus dem Chatverlauf generiert.</p>
          </section>
        )}

        {/* -------------------------- Form body ---------------------------- */}
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[1.75fr_1fr]">
            {/* LEFT column */}
            <div className="flex flex-col gap-4">
              <SectionCard title="Stammdaten">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={LABEL_CLASS}>
                          Name<span className="text-primary">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="Name" {...field} disabled={!isEditable} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="gender"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={LABEL_CLASS}>Geschlecht</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value} disabled={!isEditable}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Wählen Sie ein Geschlecht aus" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {(creationEntities?.genders ?? []).map((gender) => (
                              <SelectItem key={gender} value={gender}>
                                {Genders[gender as unknown as keyof typeof Genders]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="street"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={LABEL_CLASS}>Straße</FormLabel>
                        <FormControl>
                          <Input placeholder="Straße" {...field} disabled={!isEditable} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="postCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={LABEL_CLASS}>Postleitzahl</FormLabel>
                        <FormControl>
                          <Input placeholder="Postleitzahl" {...field} disabled={!isEditable} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="placeName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={LABEL_CLASS}>Wohnort</FormLabel>
                        <FormControl>
                          <Input placeholder="Wohnort" {...field} disabled={!isEditable} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Standort — editable on create and on edit. Changing it on an existing
                      applicant moves them, which also hands them to the contact person of the
                      target location (the contact lives on the location, not on the applicant). */}
                  <FormField
                    control={form.control}
                    name="belongsToCompanyLocationId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={LABEL_CLASS}>
                          Standort
                          {!user?.permittedContentWithoutCompanyLocationAssignment && (
                            <span className="text-primary">*</span>
                          )}
                        </FormLabel>
                        <Select
                          disabled={!isEditable}
                          onValueChange={async (value) => {
                            // Remember what was picked before the list is swapped out, so the
                            // same job at the target location can be matched by title.
                            const previousTitle = selectedLocationsJobOffers.find(
                              (offer) => offer._id === form.getValues("belongsToJobOfferId"),
                            )?.value;

                            field.onChange(value);
                            // The job offers belong to the location — the previous pick cannot survive.
                            form.setValue("belongsToJobOfferId", "");

                            if (value === "undefined") {
                              setSelectedLocationsJobOffers([]);
                              return;
                            }

                            const offers = await getJobOffersByLocationId(value);
                            form.setValue("belongsToJobOfferId", pickJobOfferForLocation(offers, value, previousTitle));
                          }}
                          value={field.value || "undefined"}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Wähle den Standort aus" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {user?.permittedContentWithoutCompanyLocationAssignment && (
                              <SelectItem key="-1" value="undefined">
                                Kein Standort ausgewählt
                              </SelectItem>
                            )}
                            {(creationEntities?.companyLocations ?? []).map((location) => (
                              <SelectItem key={location._id} value={location._id}>
                                {`${location.value}${location.isArchive ? " - Archiviert" : ""}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Beworben als — editable on create and on edit */}
                  <FormField
                    control={form.control}
                    name="belongsToJobOfferId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={LABEL_CLASS}>Beworben als</FormLabel>
                        {jobOffersLoading ? (
                          <div className="flex h-[42px] items-center">
                            <Loader2 className="h-4 w-4 animate-spin text-primary" />
                          </div>
                        ) : (
                          <>
                            <Select
                              disabled={!isEditable}
                              onValueChange={field.onChange}
                              value={field.value || "undefined"}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Wählen Sie das Jobangebot aus" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem key="-1" value="undefined">
                                  Kein Jobangebot ausgewählt
                                </SelectItem>
                                {selectedLocationsJobOffers?.map((jobOffer) => (
                                  <SelectItem key={jobOffer._id} value={jobOffer._id}>
                                    {`${jobOffer.value}${jobOffer.isArchive ? " - Archiviert" : ""}`}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </>
                        )}
                      </FormItem>
                    )}
                  />

                  {item && assignmentChanged && (
                    <p className="text-xs text-amber-700 sm:col-span-2">
                      Der Bewerber wird beim Speichern verschoben. Ab dann ist der Ansprechpartner des neuen
                      Standorts zuständig.
                    </p>
                  )}

                  {/* Quelle — editable select on create, read-only on edit */}
                  {item ? (
                    <ReadonlyField label="Quelle" value={item.sourceTitle} />
                  ) : (
                    <FormField
                      control={form.control}
                      name="source"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className={LABEL_CLASS}>
                            Quelle<span className="text-primary">*</span>
                          </FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Wählen Sie die Quelle aus" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {(creationEntities?.sources ?? []).map((source, index) => (
                                <SelectItem key={index} value={source.id}>
                                  {source.value === "None" ? "Keine Quelle ausgewählt" : source.value}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </div>
              </SectionCard>

              {/* Dokumente */}
              {item && (
                <ApplicantDocuments
                  applicantId={item._id}
                  initialDocuments={item.documents}
                  canUpload={!!isEditable}
                />
              )}

              {/* Kommunikationsverlauf — unchanged logic & rendering */}
              {item && (
                <SectionCard title="Kommunikationsverlauf">
                  {item.applicantLogs && item.applicantLogs.length > 0 ? (
                    <ol className="relative max-h-[360px] overflow-y-auto pr-2">
                      {item.applicantLogs.map((log: ApplicantLog, index: number) => {
                        const isMailSent = log.event === ("APPLICANT_MAIL_SENT" as unknown as ApplicantLogEvent);
                        let eventDescription = ApplicantLogEvent[
                          log.event as unknown as keyof typeof ApplicantLogEvent
                        ].replaceAll("{INFORMATION}", log.information ?? "");
                        if (log.information) {
                          eventDescription = `${eventDescription} - ${log.information}`;
                        }
                        const timestamp = format(new Date(log.createdAt), "dd.MM.yyyy HH:mm");
                        const active = index === 0;

                        return (
                          <li
                            key={log._id}
                            className="relative pb-5 pl-7 last:pb-0 before:absolute before:left-[5px] before:top-4 before:-bottom-1 before:w-0.5 before:bg-slate-200 before:content-[''] last:before:hidden"
                          >
                            <span
                              className={cn(
                                "absolute left-0 top-1 h-3 w-3 rounded-full border-[3px] bg-white",
                                active ? "border-primary shadow-[0_0_0_4px_#e6f5fb]" : "border-slate-300",
                              )}
                            />
                            <div className={cn("text-[13.5px] font-semibold", active ? "text-[#0b6f95]" : "text-slate-800")}>
                              {isMailSent ? (
                                <span
                                  className="cursor-pointer transition-colors hover:text-primary hover:underline"
                                  onClick={() => {
                                    setSelectedMailLogId(log._id);
                                    setIsMailLogDialogOpen(true);
                                  }}
                                >
                                  {eventDescription}
                                </span>
                              ) : (
                                eventDescription
                              )}
                            </div>
                            <div className="mt-0.5 text-xs tabular-nums text-slate-500">{timestamp}</div>
                          </li>
                        );
                      })}
                    </ol>
                  ) : (
                    <span className="text-sm text-gray-400">Keine Einträge vorhanden</span>
                  )}
                </SectionCard>
              )}
            </div>

            {/* RIGHT column */}
            <div className="flex flex-col gap-4">
              <SectionCard title="Status & Kontakt">
                <div className="flex flex-col gap-4">
                  <ReadonlyField label="Bewerbungsdatum" value={formatDate(item?.applicationDate)} />

                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={LABEL_CLASS}>
                          Status der Bewerbung<span className="text-primary">*</span>
                        </FormLabel>
                        <Select
                          disabled={!isEditable}
                          onValueChange={field.onChange}
                          defaultValue={
                            field.value
                              ? field.value
                              : (creationEntities?.status?.find((status) => status.default)?.id ?? undefined)
                          }
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Wählen Sie den Status aus" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {groupStatusOptions(creationEntities?.status ?? []).map((group) => (
                              <SelectGroup key={group.label}>
                                <SelectLabel className="text-xs font-semibold uppercase tracking-wide text-slate-900">
                                  {group.label}
                                </SelectLabel>
                                {group.items.map((status) => (
                                  <SelectItem key={status.id} value={status.id} className="pl-6">
                                    {status.value}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Bewertung (Ampel) — bound to valuation */}
                  <FormField
                    control={form.control}
                    name="valuation"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={LABEL_CLASS}>Bewertung (Ampel)</FormLabel>
                        <div className="grid grid-cols-3 gap-2">
                          {AMPEL_ORDER.map((key) => {
                            const meta = VALUATION_META[key];
                            const active = field.value === key;
                            return (
                              <button
                                key={key}
                                type="button"
                                disabled={!isEditable}
                                onClick={() => field.onChange(active ? "" : key)}
                                className={cn(
                                  "flex items-center justify-center gap-2 rounded-lg border px-2 py-2 text-sm font-semibold transition",
                                  active
                                    ? TONE[meta.tone].activeSeg
                                    : "border-slate-200 bg-slate-50 text-slate-500 hover:border-slate-300",
                                  !isEditable && "cursor-not-allowed opacity-60",
                                )}
                              >
                                <span
                                  className={cn(
                                    "h-2.5 w-2.5 rounded-full",
                                    active ? TONE[meta.tone].dot : "bg-slate-300",
                                  )}
                                />
                                {meta.label}
                              </button>
                            );
                          })}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={LABEL_CLASS}>Telefon</FormLabel>
                        <FormControl>
                          <Input placeholder="Telefonnummer bitte mit +49 angeben" {...field} disabled={!isEditable} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="mail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={LABEL_CLASS}>Email</FormLabel>
                        <FormControl>
                          <Input placeholder="Email" {...field} disabled={!isEditable} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="notes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={LABEL_CLASS}>Notizen</FormLabel>
                        <FormControl>
                          <Textarea rows={5} placeholder="Interne Notizen…" {...field} disabled={!isEditable} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </SectionCard>
            </div>
          </div>

          {/* Sticky action bar — always reachable while scrolling.
              left-[82px] matches the fixed sidebar width (see RootWrapper marginLeft). */}
          <div className="fixed bottom-0 left-[82px] right-0 z-[5] border-t border-slate-200 bg-white/85 px-8 py-3.5 shadow-[0_-8px_24px_-18px_rgba(15,27,45,0.45)] backdrop-blur">
            <div className="flex items-center justify-end gap-3">
              <Button type="button" variant="outline" className="gap-2" onClick={() => navigate("/bewerber")}>
                <X className="h-4 w-4" />
                Schließen
              </Button>
              <Button
                type="submit"
                className="gap-2"
                disabled={form.formState.isSubmitting || (item ? !item.isEditable : false)}
              >
                <Save className="h-4 w-4" />
                Speichern
              </Button>
            </div>
          </div>
        </form>

        {item && emailIsOpen && (
          <MailDialog
            item={item}
            isOpen={emailIsOpen}
            setEmailIsOpen={setEmailIsOpen}
            onSent={() => {
              setEmailIsOpen(false);
              onSaved?.();
            }}
          />
        )}
        {item?.isAiWhatsAppApplicant && (
          <WhatsAppChatPanel
            item={item}
            isOpen={chatOpen}
            onClose={() => setChatOpen(false)}
            canSend={!!isEditable}
            onCompose={() => setWaMessageOpen(true)}
          />
        )}
        {item?.isAiWhatsAppApplicant && isEditable && waMessageOpen && (
          <WhatsAppMessageDialog item={item} isOpen={waMessageOpen} onClose={() => setWaMessageOpen(false)} />
        )}
        {item && selectedMailLogId && (
          <MailLogDialog
            applicantId={item._id}
            logId={selectedMailLogId}
            isOpen={isMailLogDialogOpen}
            onClose={() => {
              setIsMailLogDialogOpen(false);
              setSelectedMailLogId(null);
            }}
          />
        )}
      </div>
    </Form>
  );
};

const MailDialog = ({
  item,
  isOpen,
  setEmailIsOpen,
  onSent,
}: {
  item: ApplicantDetailsResponseDto;
  isOpen: boolean;
  setEmailIsOpen: Dispatch<SetStateAction<boolean>>;
  onSent?: () => void;
}) => {
  const { apiRequest } = useApiRequest();
  const [mailTemplates, setMailTemplates] = useState<ApplicantMailTemplatesResponseDto>();

  const getMailTemplates = useCallback(async () => {
    const response = await apiRequest<ApplicantMailTemplatesResponseDto>(
      `applicants/${item._id}/mails/mail-templates`,
      "GET",
    );
    if (response.data) {
      setMailTemplates(response.data);
    } else {
      setEmailIsOpen(false);
    }
  }, [apiRequest, item._id, setEmailIsOpen]);

  useEffect(() => {
    getMailTemplates();
  }, [getMailTemplates]);

  return (
    <Dialog onOpenChange={(value) => setEmailIsOpen(value)} open={isOpen}>
      {mailTemplates && (
        <DialogContent className="min-w-[750px]">
          <MailModal applicantId={item._id} mailTemplates={mailTemplates} onSent={onSent} />
        </DialogContent>
      )}
    </Dialog>
  );
};

export default ApplicantEditor;
